import { useEffect } from "react";

import {
  CheckCircle,
  Warn,
} from "@hl/base-components/lib/assets/icons.generated";
import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import { STATUS_COLOR, TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import CopyButton from "@hl/shared-features/lib/features/widgets/CopyButton";
import {
  isTestnetNetwork,
  networkLookup,
} from "@hl/shared-features/lib/utils/blockExplorer";
import {
  UnstyledButton,
  Text,
  Modal,
  Button,
  Switch,
  Group,
  Card,
  Box,
  TextInput,
  Space,
  Paper,
  Anchor,
  Loader,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";

import {
  GetManageTokenCollectionDocument,
  useUpdateCollectionOffChainMutation,
  useGetManageTokenCollectionQuery,
  useCrossmintStatusQuery,
} from "../queries.graphql.generated";

type FormValues = {
  creditCardEnabled: boolean;
  cmCollectionId?: string;
};

type DataProps = {
  collectionId: string;
  creditCardEnabled: boolean;
};

const guidRegex =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const CROSSMINT_HELP_URL =
  "https://support.highlight.xyz/knowledge-base/highlight-toolkit/toolkit/mint-tools/credit-card-enablement";

const CrossmintModal = ({
  opened,
  close,
  collectionId,
  creditCardEnabled,
}: {
  opened: boolean;
  close: () => void;
} & DataProps) => {
  const { data, loading: loadingForm } = useGetManageTokenCollectionQuery({
    variables: {
      collectionId,
      first: 10,
      firstHashes: 99999,
    },
  });
  const collection = data?.getCollection;
  const contractAddress = collection?.contract?.address;

  const form = useForm<FormValues>({
    initialValues: {
      creditCardEnabled,
      cmCollectionId: collection?.crossmint?.collectionId,
    },
    validate: {
      cmCollectionId: (id, values) => {
        if (values.creditCardEnabled) {
          if (!id) {
            return "This field is required";
          }
          if (!guidRegex.test(id)) {
            return "Incorrect value for id";
          }
        }
        return null;
      },
    },
  });

  const network = collection?.chainId
    ? networkLookup(collection?.chainId)
    : null;
  const isTestnet = network ? isTestnetNetwork(network.type) : false;

  const { cmCollectionId } = form.values;
  const {
    data: cmStatusData,
    loading: cmStatusLoading,
    error: cmStatusError,
  } = useCrossmintStatusQuery({
    variables: {
      cmCollectionId: cmCollectionId!,
      isTestnet,
    },
    skip: !collection || !cmCollectionId || !form.isValid("cmCollectionId"),
  });
  const cmStatus = cmStatusData?.crossmintVerificationStatus;
  const isSellerVerified = cmStatus?.seller?.status === "verified";
  const isCollectionVerified = cmStatus?.collection?.status === "verified";
  let statusWarning: string | null = null;
  if (cmStatusError) {
    statusWarning =
      "Error while verifying, please make sure that ID is correct";
  } else if (cmStatus && !isSellerVerified) {
    statusWarning = "Project owner is not verified";
  } else if (cmStatus && !isCollectionVerified) {
    statusWarning = "Collection is not verified";
  }

  useEffect(() => {
    if (statusWarning) {
      form.setFieldError("cmCollectionId", statusWarning);
    } else {
      form.clearFieldError("cmCollectionId");
    }
  }, [statusWarning]);

  const [updateCollection, { loading: updating, error }] =
    useUpdateCollectionOffChainMutation({
      refetchQueries: [GetManageTokenCollectionDocument],
    });

  return (
    <Modal
      opened={opened}
      onClose={close}
      title="Credit card payments setup"
      size="lg"
    >
      <form
        onSubmit={form.onSubmit(async (values) => {
          if (!collection) {
            return;
          }
          await updateCollection({
            variables: {
              collectionId,
              data: {
                creditCardEnabled: values.creditCardEnabled,
                crossmint: values.creditCardEnabled
                  ? {
                      collectionId: values.cmCollectionId ?? "",
                      environment: isTestnet ? "staging" : "production",
                    }
                  : null,
              },
            },
          });
          close();
        })}
      >
        <Card p="xs">
          <Group position="apart" align="center">
            <Text size="sm">Enable credit card payments</Text>
            <Switch
              sx={{
                input: {
                  height: "100%",
                },
              }}
              {...form.getInputProps("creditCardEnabled")}
              checked={form.getInputProps("creditCardEnabled").value}
            />
          </Group>
        </Card>
        {form.values.creditCardEnabled && (
          <>
            <Text size="sm" mt={40}>
              Follow{" "}
              <Anchor href={CROSSMINT_HELP_URL} target="_blank" fw={500}>
                these instructions
              </Anchor>{" "}
              to configure credit card payments using{" "}
              <Anchor
                href={
                  isTestnet
                    ? "https://staging.crossmint.com/"
                    : "https://crossmint.com/"
                }
                target="_blank"
                rel="noreferrer"
              >
                <Text fw={500}>Crossmint</Text>
              </Anchor>
            </Text>
            <Box pt={40}>
              <Text size="sm" fw={500}>
                Contract address
              </Text>
              <Text size="sm">
                Use this contract address when following the instructions above
              </Text>
              <Paper p="xs" shadow="none" withBorder mt="sm">
                <Group position="apart" align="center">
                  <Text color={TEXT_COLOR.SECONDARY}>{contractAddress}</Text>
                  <CopyButton copyValue={contractAddress ?? ""} />
                </Group>
              </Paper>
            </Box>
            <Space h={40} />
            <TextInput
              placeholder="Enter Crossmint collection ID"
              spellCheck={false}
              label="Crossmint collection ID"
              {...form.getInputProps("cmCollectionId")}
              styles={(theme) => {
                if (!cmStatusError && !statusWarning) {
                  return {};
                }
                const warnColor = theme.fn.themeColor(STATUS_COLOR.WARNING);
                return {
                  error: {
                    color: warnColor,
                  },
                  input: {
                    "&[aria-invalid=true]": {
                      color: warnColor,
                      borderColor: warnColor,
                    },
                  },
                };
              }}
              rightSection={
                !cmCollectionId ||
                !form.isValid("cmCollectionId") ? null : cmStatusLoading ? (
                  <Loader />
                ) : cmStatusError || statusWarning ? (
                  <Warn />
                ) : (
                  <CheckCircle color={"green"} />
                )
              }
            />
          </>
        )}

        <ModalSection>
          <Button
            type="submit"
            size="xl"
            fullWidth
            loading={updating}
            disabled={loadingForm}
          >
            Save
          </Button>
        </ModalSection>
        {error && (
          <Text color={TEXT_COLOR.TERTIARY}>Error: failed to save</Text>
        )}
      </form>
    </Modal>
  );
};

export const OpenCrossmintModalButton = ({
  text,
  ...props
}: { text: string } & DataProps) => {
  const [opened, { open, close }] = useDisclosure(false);

  return (
    <>
      <UnstyledButton onClick={open}>
        <Text size="sm" fw={500}>
          {text}
        </Text>
      </UnstyledButton>
      {opened && <CrossmintModal close={close} opened={opened} {...props} />}
    </>
  );
};
